import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FiveMovementPlatformPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pledgeData, setPledgeData] = useState(null);
  const [communityStats, setCommunityStats] = useState({
    totalPledges: 47,
    totalWeeksLogged: 312,
    activeStreaks: 23
  });
  const [showPledgeModal, setShowPledgeModal] = useState(false);
  const [weeklyAmount, setWeeklyAmount] = useState(5);
  
  // Fetch user's pledge data
  useEffect(() => {
    const fetchPledgeData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API}/movement/progress`, {
          headers: getAuthHeader()
        });
        if (response.data.has_pledged) {
          setPledgeData({
            current_streak: response.data.streak || 0,
            total_saved: (response.data.weeks_logged || 0) * 5,
            weekly_amount: 5,
            last_logged: response.data.last_check_in
          });
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error fetching pledge:', error);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPledgeData();
  }, [user, getAuthHeader]);

  // Make a pledge
  const handleMakePledge = async () => {
    if (!user) {
      toast.error('Please log in to make a pledge');
      return;
    }
    try {
      const response = await axios.post(`${API}/movement/pledge`, {}, {
        headers: getAuthHeader()
      });
      setPledgeData({
        current_streak: response.data.streak || 0,
        total_saved: (response.data.weeks_logged || 0) * weeklyAmount,
        weekly_amount: weeklyAmount,
        last_logged: response.data.last_check_in
      });
      setShowPledgeModal(false);
      toast.success('Welcome to the $5 Movement! Your journey begins now.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pledge');
    }
  };

  // Log a week
  const handleLogWeek = async () => {
    if (!pledgeData) return;
    try {
      const response = await axios.post(`${API}/movement/checkin`, {}, {
        headers: getAuthHeader()
      });
      setPledgeData({
        current_streak: response.data.streak || 0,
        total_saved: (response.data.weeks_logged || 0) * 5,
        weekly_amount: 5,
        last_logged: response.data.last_check_in
      });
      toast.success(`Week ${response.data.streak} logged! Keep building!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log week');
    }
  };

  // Calculate milestone badges
  const getMilestoneBadges = (streak) => {
    const badges = [];
    if (streak >= 4) badges.push({ weeks: 4, label: 'Month 1', color: '#CD7F32' });
    if (streak >= 12) badges.push({ weeks: 12, label: 'Quarter', color: '#C0C0C0' });
    if (streak >= 26) badges.push({ weeks: 26, label: 'Half Year', color: '#FFD700' });
    if (streak >= 52) badges.push({ weeks: 52, label: 'Full Year', color: '#E5E4E2' });
    return badges;
  };

  // Calculate progress to next milestone
  const getNextMilestone = (streak) => {
    if (streak < 4) return { target: 4, label: 'Month 1' };
    if (streak < 12) return { target: 12, label: 'Quarter' };
    if (streak < 26) return { target: 26, label: 'Half Year' };
    if (streak < 52) return { target: 52, label: 'Full Year' };
    return { target: streak + 52, label: 'Year ' + Math.ceil((streak + 1) / 52) };
  };

  const stats = [
    { value: `${communityStats.totalPledges}+`, label: 'Members Pledged' },
    { value: '$5', label: '/ Week Commitment' },
    { value: '52', label: '-Week Challenge' },
    { value: '100%', label: 'Community-Powered' }
  ];

  const steps = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Make the Pledge',
      description: 'Commit to investing $5 every week. Your word is your foundation.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Track Your Streak',
      description: 'Log each week and watch your streak grow. Milestones unlock badges at 4, 12, 26, and 52 weeks.'
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'See the Community Move',
      description: 'Watch the collective commitment grow in real time. You are not doing this alone.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0d0d0d', fontFamily: "'Inter', sans-serif" }}>
      <link 
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap" 
        rel="stylesheet" 
      />

      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#111111' }}>
        <span className="text-xl font-bold tracking-wide" style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}>
          POWR
        </span>
        <Link to="/community" className="text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: '#c9a84c' }}>
          ← Back to Community
        </Link>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-16 text-center max-w-4xl mx-auto">
        <p className="text-xs tracking-[0.2em] uppercase mb-6" style={{ color: '#c9a84c' }}>
          POWR COMMUNITY · INVESTMENT TOOLS
        </p>
        <h1 className="text-4xl md:text-5xl lg:text-6xl mb-6" style={{ color: '#ffffff', fontFamily: "'Playfair Display', serif", fontWeight: 500, lineHeight: 1.2 }}>
          The $5 Movement
        </h1>
        <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: '#e5e5e5' }}>
          Every dollar you invest is a step toward legacy. Join the community challenge and make your money work — starting with just $5 a week.
        </p>
        
        {!user ? (
          <Link
            to="/login"
            className="inline-block font-bold transition-all hover:scale-105"
            style={{ backgroundColor: '#c9a84c', color: '#000000', fontWeight: 700, borderRadius: '4px', padding: '14px 32px' }}
          >
            Log In to Join the Movement
          </Link>
        ) : !pledgeData ? (
          <button
            onClick={() => setShowPledgeModal(true)}
            className="inline-block font-bold transition-all hover:scale-105"
            style={{ backgroundColor: '#c9a84c', color: '#000000', fontWeight: 700, borderRadius: '4px', padding: '14px 32px' }}
          >
            Join the $5 Movement
          </button>
        ) : (
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-lg" style={{ backgroundColor: 'rgba(201, 168, 76, 0.15)', border: '1px solid #c9a84c' }}>
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#c9a84c' }}>
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            <span style={{ color: '#c9a84c', fontWeight: 600 }}>You're in the Movement!</span>
          </div>
        )}
        
        <p className="text-xs mt-4 opacity-70" style={{ color: '#e5e5e5' }}>
          Free to join · No real money handled · Commitment tracker only
        </p>
      </section>

      {/* Community Stats Bar */}
      <section className="py-12 px-6" style={{ backgroundColor: '#111111' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#c9a84c' }}>{stat.value}</p>
              <p className="text-sm" style={{ color: '#ffffff' }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Personal Dashboard */}
      {user && pledgeData && (
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}>
              Your Movement Dashboard
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {/* Current Streak */}
              <div className="p-6 rounded-xl text-center" style={{ backgroundColor: '#111111', border: '1px solid #222' }}>
                <p className="text-sm uppercase tracking-wider mb-2" style={{ color: '#888' }}>Current Streak</p>
                <p className="text-5xl font-bold mb-2" style={{ color: '#c9a84c' }}>{pledgeData.current_streak}</p>
                <p className="text-sm" style={{ color: '#fff' }}>weeks</p>
              </div>
              
              {/* Total Saved */}
              <div className="p-6 rounded-xl text-center" style={{ backgroundColor: '#111111', border: '1px solid #222' }}>
                <p className="text-sm uppercase tracking-wider mb-2" style={{ color: '#888' }}>Total Committed</p>
                <p className="text-5xl font-bold mb-2" style={{ color: '#22c55e' }}>${pledgeData.total_saved}</p>
                <p className="text-sm" style={{ color: '#fff' }}>lifetime</p>
              </div>
              
              {/* Weekly Amount */}
              <div className="p-6 rounded-xl text-center" style={{ backgroundColor: '#111111', border: '1px solid #222' }}>
                <p className="text-sm uppercase tracking-wider mb-2" style={{ color: '#888' }}>Weekly Pledge</p>
                <p className="text-5xl font-bold mb-2" style={{ color: '#fff' }}>${pledgeData.weekly_amount}</p>
                <p className="text-sm" style={{ color: '#fff' }}>per week</p>
              </div>
            </div>

            {/* Progress to Next Milestone */}
            {(() => {
              const nextMilestone = getNextMilestone(pledgeData.current_streak);
              const progress = (pledgeData.current_streak / nextMilestone.target) * 100;
              return (
                <div className="p-6 rounded-xl mb-8" style={{ backgroundColor: '#111111', border: '1px solid #222' }}>
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm" style={{ color: '#888' }}>Progress to {nextMilestone.label}</p>
                    <p className="text-sm font-bold" style={{ color: '#c9a84c' }}>{pledgeData.current_streak} / {nextMilestone.target} weeks</p>
                  </div>
                  <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#222' }}>
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(progress, 100)}%`, backgroundColor: '#c9a84c' }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Earned Badges */}
            {getMilestoneBadges(pledgeData.current_streak).length > 0 && (
              <div className="p-6 rounded-xl mb-8" style={{ backgroundColor: '#111111', border: '1px solid #222' }}>
                <p className="text-sm uppercase tracking-wider mb-4" style={{ color: '#888' }}>Earned Badges</p>
                <div className="flex flex-wrap gap-4">
                  {getMilestoneBadges(pledgeData.current_streak).map((badge, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(201, 168, 76, 0.1)', border: `2px solid ${badge.color}` }}>
                      <svg className="w-5 h-5" fill={badge.color} viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      <span className="text-sm font-semibold" style={{ color: badge.color }}>{badge.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Log Week Button */}
            <div className="text-center">
              <button
                onClick={handleLogWeek}
                className="px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105"
                style={{ backgroundColor: '#c9a84c', color: '#000' }}
              >
                Log This Week's $5
              </button>
              <p className="text-xs mt-3" style={{ color: '#666' }}>
                Last logged: {pledgeData.last_logged ? new Date(pledgeData.last_logged).toLocaleDateString() : 'Never'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-16 px-6" style={{ backgroundColor: '#111111' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}>
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: 'rgba(201, 168, 76, 0.15)', color: '#c9a84c' }}>
                  {step.icon}
                </div>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#ffffff' }}>{step.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#e5e5e5' }}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-6 text-center">
        <p className="text-xs max-w-2xl mx-auto" style={{ color: '#666666' }}>
          This tool tracks personal savings commitments only. No real money is collected, held, or invested. This is not a financial product or investment service.
        </p>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 text-center" style={{ backgroundColor: '#0a0a0a' }}>
        <p className="text-2xl font-bold mb-3" style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}>POWR</p>
        <p className="text-sm mb-4" style={{ color: '#e5e5e5' }}>Purpose · Wealth · Legacy</p>
        <p className="text-xs" style={{ color: '#666666' }}>© {new Date().getFullYear()} POWR. All rights reserved.</p>
      </footer>

      {/* Pledge Modal */}
      {showPledgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
          <div className="w-full max-w-md p-8 rounded-2xl" style={{ backgroundColor: '#111111', border: '1px solid #222' }}>
            <h3 className="text-2xl font-bold mb-2 text-center" style={{ color: '#c9a84c', fontFamily: "'Playfair Display', serif" }}>
              Make Your Pledge
            </h3>
            <p className="text-sm text-center mb-8" style={{ color: '#888' }}>
              Commit to investing in yourself every week
            </p>
            
            <div className="mb-8">
              <label className="block text-sm mb-3" style={{ color: '#fff' }}>Weekly Investment Amount</label>
              <div className="flex gap-3">
                {[5, 10, 20, 50].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setWeeklyAmount(amount)}
                    className="flex-1 py-3 rounded-lg font-bold transition-all"
                    style={{
                      backgroundColor: weeklyAmount === amount ? '#c9a84c' : '#222',
                      color: weeklyAmount === amount ? '#000' : '#fff',
                      border: weeklyAmount === amount ? 'none' : '1px solid #333'
                    }}
                  >
                    ${amount}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 rounded-lg mb-8" style={{ backgroundColor: '#0d0d0d' }}>
              <p className="text-sm" style={{ color: '#888' }}>In 52 weeks, you'll have committed:</p>
              <p className="text-3xl font-bold" style={{ color: '#22c55e' }}>${weeklyAmount * 52}</p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowPledgeModal(false)}
                className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: '#222', color: '#fff' }}
              >
                Cancel
              </button>
              <button
                onClick={handleMakePledge}
                className="flex-1 py-3 rounded-lg font-bold"
                style={{ backgroundColor: '#c9a84c', color: '#000' }}
              >
                Make My Pledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiveMovementPlatformPage;
